/**
 * Auth configuration and helpers.
 *
 * Catty uses a server-side OAuth flow:
 * 1. SPA builds an Entra authorisation URL and navigates to it.
 * 2. Entra redirects to /auth/callback (handled by the Lambda).
 * 3. Lambda exchanges the code for tokens, sets an HttpOnly session cookie,
 *    and redirects back to /.
 * 4. SPA detects authentication by calling GET /api/user (cookie is automatic).
 */

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID as string;
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID as string;

/**
 * Graph OAuth scope sets.
 * OIDC scopes are present at every level. Levels are additive.
 */
export const GRAPH_SCOPE_LEVEL = {
  identity: [
    'openid', 'profile', 'email', 'offline_access',
  ].join(' '),
  register: [
    'openid', 'profile', 'email', 'offline_access',
    'Mail.Read',
  ].join(' '),
  onboarding: [
    'openid', 'profile', 'email', 'offline_access',
    'Mail.ReadWrite',
    'MailboxFolder.Read',
  ].join(' '),
  activate: [
    'openid', 'profile', 'email', 'offline_access',
    'Mail.ReadWrite',
    'MailboxFolder.Read',
    'MailboxSettings.Read',
  ].join(' '),
} as const;

export type ScopeLevel = keyof typeof GRAPH_SCOPE_LEVEL;

interface AuthUrlOptions {
  scopeLevel?: ScopeLevel;
  prompt?: 'consent' | 'login' | 'select_account';
}

/** Build the Entra admin consent URL for org-wide permission approval. */
export function buildAdminConsentUrl(): string {
  const redirectUri = `${window.location.origin}/auth/callback`;

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri,
  });

  return `https://login.microsoftonline.com/common/adminconsent?${params.toString()}`;
}

/** Build the Entra authorisation URL for the redirect-based sign-in flow. */
export function buildAuthUrl(options: AuthUrlOptions = {}): string {
  const scopeLevel = options.scopeLevel ?? 'identity';
  const redirectUri = `${window.location.origin}/auth/callback`;

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: GRAPH_SCOPE_LEVEL[scopeLevel],
    response_mode: 'query',
  });

  if (options.prompt) {
    params.set('prompt', options.prompt);
  }

  return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
}

export interface AuthUser {
  id: string;
  email: string;
  displayName?: string;
  scopeLevel?: ScopeLevel;
}

/** Fetch the current authenticated user from the API. Returns null if unauthenticated. */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const resp = await fetch('/api/user', { credentials: 'include' });
    if (!resp.ok) return null;
    const body = (await resp.json()) as { user: AuthUser };
    return body.user;
  } catch {
    return null;
  }
}
